/**
 * Autocomplete engine for the rule editor — pure, so the token/suggestion logic
 * is unit-tested without a DOM.
 *
 * Two languages share it: the expression field is JSONata over `{ bundle }` with
 * `$lists`; the message field is LiquidJS over `{ bundle, matched, lists }`.
 * Completions are driven off a real, navigable value:
 *   - when a sample bet is loaded, the ACTUAL bundle — so per-game bet columns
 *     and real ClickHouse fields show up, not a guess;
 *   - otherwise STATIC_BUNDLE below, a shape-only stand-in.
 */

export type EditorMode = 'jsonata' | 'liquid';

export interface SuggestModel {
  /** The bundle to navigate — real (loaded bet) or STATIC_BUNDLE. */
  bundle: unknown;
  /** Named lists from the draft rule, reachable as `$lists.<name>` / `lists.<name>`. */
  lists: Record<string, unknown[]>;
  /** The last preview's `matched` value — the Liquid template's `matched`. */
  matched: unknown;
}

export type SuggestionKind = 'root' | 'field' | 'func' | 'filter' | 'tag';

export interface Suggestion {
  label: string;
  /** Text inserted in place of the partial token. */
  apply: string;
  kind: SuggestionKind;
  detail?: string;
}

export interface TokenContext {
  kind: 'path' | 'filter' | 'tag' | 'none';
  /** Replacement range: [start, end] covers only the partial being typed. */
  start: number;
  end: number;
  partial: string;
  /** Dotted path segments to the LEFT of the partial (context for path completion). */
  prefix: string[];
}

const NONE: TokenContext = { kind: 'none', start: 0, end: 0, partial: '', prefix: [] };

// A dotted identifier chain ending at the caret: `bundle`, `bundle.transactions.`,
// `bundle.transactions.da`, `$lists.crypto`.
const CHAIN = /(?:[$A-Za-z_][\w$]*)(?:\.[\w$]*)*$/;

/** Odd number of unescaped single quotes before the caret → inside a string. */
function insideString(before: string): boolean {
  const m = before.match(/'/g);
  return m ? m.length % 2 === 1 : false;
}

export function tokenAt(text: string, caret: number, mode: EditorMode): TokenContext {
  const before = text.slice(0, caret);

  if (mode === 'liquid') {
    const open = Math.max(before.lastIndexOf('{{'), before.lastIndexOf('{%'));
    if (open === -1) return NONE;
    const isTag = before.lastIndexOf('{%') > before.lastIndexOf('{{');
    const seg = before.slice(open + 2);
    // Already closed before the caret → not inside an output/tag.
    if (/\}\}|%\}/.test(seg)) return NONE;

    // Filter position: after a `|`, typing the filter name.
    const pipe = seg.lastIndexOf('|');
    if (pipe !== -1) {
      const after = seg.slice(pipe + 1);
      const fm = after.match(/^\s*([\w-]*)$/);
      if (fm) {
        const partial = fm[1];
        return { kind: 'filter', start: caret - partial.length, end: caret, partial, prefix: [] };
      }
    }

    // Right after `{%`, the first word is a tag name.
    if (isTag) {
      const tm = seg.match(/^\s*([\w]*)$/);
      if (tm) {
        const partial = tm[1];
        return { kind: 'tag', start: caret - partial.length, end: caret, partial, prefix: [] };
      }
    }

    // Path completion inside the braces. Unlike JSONata, an empty token here
    // (just after `{{`) should still list the roots, so fall back to one.
    const match = before.match(CHAIN);
    if (!match) return { kind: 'path', start: caret, end: caret, partial: '', prefix: [] };
    return chainToken(match[0], caret);
  }

  if (insideString(before)) return NONE;

  const match = before.match(CHAIN);
  if (!match) return NONE;
  return chainToken(match[0], caret);
}

function chainToken(chain: string, caret: number): TokenContext {
  const segs = chain.split('.');
  const partial = segs[segs.length - 1];
  const prefix = segs.slice(0, -1);
  return { kind: 'path', start: caret - partial.length, end: caret, partial, prefix };
}

/** Merge a sample of array elements into one object so we can list row fields. */
function mergeElements(arr: unknown[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const el of arr.slice(0, 25)) {
    if (el && typeof el === 'object' && !Array.isArray(el)) Object.assign(out, el);
  }
  return out;
}

function typeName(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  return typeof v;
}

function resolvePath(roots: Record<string, unknown>, prefix: string[]): unknown {
  let v: unknown = roots;
  for (const seg of prefix) {
    if (Array.isArray(v)) v = mergeElements(v);
    if (v && typeof v === 'object') v = (v as Record<string, unknown>)[seg];
    else return undefined;
  }
  return v;
}

function childrenOf(roots: Record<string, unknown>, prefix: string[]): { name: string; detail: string }[] {
  const resolved = resolvePath(roots, prefix);
  const obj = Array.isArray(resolved) ? mergeElements(resolved) : resolved;
  if (!obj || typeof obj !== 'object') return [];
  return Object.keys(obj).map((name) => ({ name, detail: typeName((obj as Record<string, unknown>)[name]) }));
}

function rootsFor(mode: EditorMode, model: SuggestModel): Record<string, unknown> {
  return mode === 'jsonata'
    ? { bundle: model.bundle, $lists: model.lists }
    : { bundle: model.bundle, matched: model.matched, lists: model.lists };
}

const MAX_SUGGESTIONS = 30;

function rank(items: Suggestion[], partial: string): Suggestion[] {
  const p = partial.toLowerCase();
  if (!p) {
    // No partial yet: keep the source/schema order — the important fields lead.
    return items.slice(0, MAX_SUGGESTIONS);
  }
  const filtered = items.filter((i) => i.label.toLowerCase().includes(p));
  filtered.sort((a, b) => {
    const ap = a.label.toLowerCase().startsWith(p) ? 0 : 1;
    const bp = b.label.toLowerCase().startsWith(p) ? 0 : 1;
    return ap - bp || a.label.localeCompare(b.label);
  });
  return filtered.slice(0, MAX_SUGGESTIONS);
}

export function suggestionsFor(ctx: TokenContext, mode: EditorMode, model: SuggestModel): Suggestion[] {
  if (ctx.kind === 'filter') {
    return rank(
      FILTERS.map((f) => ({ label: f, apply: f, kind: 'filter' as const, detail: 'filter' })),
      ctx.partial,
    );
  }
  if (ctx.kind === 'tag') {
    return rank(
      TAGS.map((t) => ({ label: t, apply: t, kind: 'tag' as const, detail: 'tag' })),
      ctx.partial,
    );
  }
  if (ctx.kind !== 'path') return [];

  const roots = rootsFor(mode, model);

  if (ctx.prefix.length === 0) {
    const rootItems: Suggestion[] = Object.keys(roots).map((name) => ({
      label: name,
      apply: name,
      kind: 'root',
      detail: name === '$lists' || name === 'lists' ? 'lists' : name === 'matched' ? 'value' : 'bundle',
    }));
    const funcItems: Suggestion[] =
      mode === 'jsonata' && ctx.partial.startsWith('$')
        ? FUNCTIONS.map((f) => ({ label: f, apply: `${f}(`, kind: 'func', detail: 'function' }))
        : [];
    return rank([...rootItems, ...funcItems], ctx.partial);
  }

  const items: Suggestion[] = childrenOf(roots, ctx.prefix).map((c) => ({
    label: c.name,
    apply: c.name,
    kind: 'field',
    detail: c.detail,
  }));
  return rank(items, ctx.partial);
}

// JSONata built-ins worth suggesting (leading `$`).
export const FUNCTIONS = [
  '$count', '$sum', '$exists', '$string', '$number', '$boolean', '$not',
  '$substring', '$contains', '$lowercase', '$uppercase', '$trim', '$split', '$join',
  '$keys', '$lookup', '$map', '$filter', '$reduce', '$sort', '$distinct', '$append', '$reverse',
  '$now', '$millis', '$toMillis', '$fromMillis', '$round', '$floor', '$ceil', '$abs',
  '$min', '$max', '$average', '$match', '$length', '$type',
];

// LiquidJS filters most useful for hint messages.
export const FILTERS = [
  'default', 'upcase', 'downcase', 'capitalize', 'size', 'first', 'last', 'join',
  'plus', 'minus', 'times', 'divided_by', 'round', 'ceil', 'floor', 'abs',
  'replace', 'truncate', 'append', 'prepend', 'escape', 'strip', 'date', 'json',
];

export const TAGS = ['if', 'elsif', 'else', 'endif', 'unless', 'endunless', 'for', 'endfor', 'assign', 'case', 'when', 'endcase'];

/**
 * Shape-only stand-in used before a sample bet is loaded. Values exist only so
 * the engine can list keys and show a type — real data (from a loaded bet) always
 * overrides this. It intentionally mirrors the guaranteed/common fields from the
 * package types; when a bet is loaded the live bundle is authoritative.
 */
const trxRow = {
  id: 0, cycle_uniq_id: '', tr_uniq_id: '', game_uuid: '', action: '', player_id: '',
  amount: '', currency: '', round_id: '', session_id: '', bet_id: '', type: '',
  freeround_id: '', client_trx_id: '', rollbacked: 0, rollback_id: '', play_service_id: '',
  player_ip: 0, description: '', created_at: '', updated_at: '',
};

export const STATIC_BUNDLE = {
  resolved: {
    play_service_id: '', game_uuid: '', bet_id: '', cycle_uniq_id: '',
    game: { game_uuid: '', type: '', name: '', rtp_variation_enabled: 0, freebet_params: {} },
  },
  correlation: { rounds: 0, traceIds: [], truncated: false },
  transactions: { status: '', error: '', data: [trxRow] },
  bet: { status: '', error: '', data: { id: 0, bet_trx_id: 0, win_trx_id: 0, rollback_trx_id: 0, rtp_variation_id: 0 } },
  roundDetails: { status: '', error: '', data: { game_type: '', transactions: [], round_result: {}, url: '' } },
  logs: { status: '', error: '', data: [{ ts: '', line: '', level: '', service_name: '', namespace: '', trace_id: '', bet_id: '', discoveredVia: '' }] },
  traces: { status: '', error: '', data: [{ traceId: '', service: '', durationMs: 0, spans: [], detailStatus: '', discoveredVia: '' }] },
  clickhouseRealtime: { status: '', error: '', data: [{ cycle_uniq_id: '', tr_uniq_id: '', play_service_id: '', action: '', amount: '', currency: '', round_id: '', session_id: '', type: '', dt: '' }] },
  clickhouseMv: { status: '', error: '', data: [{ bet_id: '', play_service_id: '', action: '', amount: '', currency: '', code: '', client_error: '', balance: '', dt: '' }] },
  clickhouseSync: { status: '', error: '', data: [{ bet_id: 0, round_id: 0, freeround_id: 0, play_service_id: '', action: '', amount: '', currency: '', dt: '' }] },
  delayDelivery: { status: '', error: '', data: [{ id: 0, payload: {}, action: '', last_try_result: '', tries: 0, archived: 0, delivered: 0, delivered_at: '', tr_uniq_id: '', cycle_uniq_id: '', play_service_id: '', last_delivery_try: '', created_at: '', updated_at: '' }] },
  sessions: { status: '', error: '', data: [{ id: 0, session_id: '', play_service_id: '', player_id: '', currency: '', game_uuid: '', locale: '', is_demo: 0, game_session_id: '', token_data: '', tokenDecoded: {}, created_at: '', updated_at: '' }] },
  freebets: { status: '', error: '', data: [{ status: '', currency: '', bet_amount: '', won_amount: '', total_freebet_count: 0, played_freebet_count: 0, type: '', type_ref_id: '', start_date: '', expire_date: '', config: {}, eligible_game_uuids: [] }] },
  rtpVariation: { status: '', error: '', data: { game_type: '', name: '', config_type: '', config_value: '', rtp: '', is_default: 0, active: 0, game_variant: '' } },
  currencyMetadata: { status: '', error: '', data: { type: '', name: '', symbol: '', display_precision: 0, amount_precision: 0, rounding: '' } },
};
